/**
 * AutoDocPage - AutoDoc.
 * Génère automatiquement de la documentation de conformité NIS2 à partir des
 * connexions Microsoft 365 / Azure. Catalogue de documents, mise en forme
 * homogène, introduction (objet + public cible) pour chaque document.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Title3, Body1, Caption1, Button, tokens,
  MessageBar, MessageBarBody, Spinner,
} from '@fluentui/react-components';
import {
  ArrowDownload24Regular, BuildingMultiple24Regular, ShieldLock24Regular,
  DocumentBulletList24Regular, Warning24Regular, Key24Regular, LockClosed24Regular,
  AppsList24Regular, Mail24Regular, Share24Regular, PeopleTeam24Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { BrandIcon } from '../components/BrandIcon';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { onboardingApi } from '../api/onboardingApi';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import { buildScanByControl } from '../reports/cyfunReportData';
import { downloadO365PlatformDocx } from '../reports/generateO365PlatformDoc';
import { downloadAzureDocx, downloadSecurityPolicyDocx, downloadDataRegisterDocx, type AutodocMeta } from '../reports/generateAutodocReports';
import { downloadMfaDocx, downloadEncryptionDocx } from '../reports/generateThematicDocs';
import { platformFactsApi, type PlatformFacts } from '../api/platformFactsApi';
import { mfaFactsApi, type MfaFacts } from '../api/mfaFactsApi';
import { entraAppsApi } from '../api/entraAppsApi';
import { downloadEntraAppsListingDocx } from '../reports/generateEntraAppsDocs';
import { m365ConfigFactsApi } from '../api/m365ConfigFactsApi';
import { downloadExoConfigDocx, downloadSpoConfigDocx, downloadTeamsConfigDocx } from '../reports/generateM365ConfigDocs';

type DocKey = 'm365' | 'azure' | 'policy' | 'register' | 'mfa' | 'encryption' | 'entraListing' | 'exo' | 'spo' | 'teams';

export function AutoDocPage() {
  const { t } = useTranslation();
  const { tenant } = useActiveTenant();
  const m365Scan = useLatestScan(tenant.id);
  const orgQ  = useQuery({ queryKey: ['onboarding-status'], queryFn: () => onboardingApi.status(), staleTime: 60_000, retry: false });
  const connQ = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const connId = connQ.data?.connections?.[0]?.id;
  const azureQ = useQuery({ queryKey: ['azure-latest', connId], queryFn: () => azureScansApi.latest(connId!), enabled: !!connId, staleTime: 10_000, retry: false });

  const [busy, setBusy] = useState<DocKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const m365Results = (m365Scan as unknown as { results?: Record<string, unknown>[]; finishedAt?: string; startedAt?: string } | null)?.results ?? [];
  const azureResults = (azureQ.data?.results ?? []) as unknown as Record<string, unknown>[];
  const hasM365Scan = m365Results.length > 0;
  const hasAzureScan = azureResults.length > 0;

  function baseMeta(): AutodocMeta {
    const o = orgQ.data;
    const scan = m365Scan as unknown as { finishedAt?: string; startedAt?: string } | null;
    return {
      orgName:      o?.organizationName || o?.companyName || tenant.name,
      tenantDomain: o?.onmsDomain || o?.defaultDomain || undefined,
      tenantId:     o?.tenantId || undefined,
      lastScanDate: scan?.finishedAt || scan?.startedAt || undefined,
      logo:         o?.companyLogo || undefined,
    };
  }

  async function run(key: DocKey) {
    setBusy(key); setError(null); setNotice(null);
    try {
      const meta = baseMeta();
      if (key === 'm365') {
        const scanByControl = buildScanByControl(m365Results, []);
        // Enrichissement Graph best-effort.
        let facts: PlatformFacts | null = null;
        try {
          const r = await platformFactsApi.get();
          if (r.success && r.facts) {
            facts = r.facts;
            if (r.warnings && r.warnings.length > 0) setNotice(`Enrichi via Microsoft Graph — ${r.warnings.length} donnée(s) non récupérée(s) (permissions/licence).`);
          } else {
            setNotice('Données Graph indisponibles — document généré depuis le scan uniquement.');
          }
        } catch {
          setNotice('Données Graph indisponibles — document généré depuis le scan uniquement.');
        }
        await downloadO365PlatformDocx({ meta: { ...meta }, scanByControl, facts });
      } else if (key === 'azure') {
        const scanByControl = buildScanByControl([], azureResults);
        await downloadAzureDocx({ meta, scanByControl });
      } else if (key === 'policy') {
        await downloadSecurityPolicyDocx({ meta });
      } else if (key === 'register') {
        await downloadDataRegisterDocx({ meta });
      } else if (key === 'mfa') {
        const scanByControl = buildScanByControl(m365Results, azureResults);
        let mfaFacts: MfaFacts | undefined;
        try {
          const r = await mfaFactsApi.get();
          if (r.success && r.facts) {
            mfaFacts = r.facts;
            if (r.warnings && r.warnings.length > 0) setNotice(`Enrichi via Microsoft Graph — ${r.warnings.length} donnée(s) non récupérée(s) (permissions).`);
          } else {
            setNotice('Données MFA live indisponibles — document généré depuis le scan uniquement.');
          }
        } catch {
          setNotice('Données MFA live indisponibles — document généré depuis le scan uniquement.');
        }
        await downloadMfaDocx({ meta, scanByControl, mfaFacts });
      } else if (key === 'encryption') {
        const scanByControl = buildScanByControl(m365Results, azureResults);
        await downloadEncryptionDocx({ meta, scanByControl });
      } else if (key === 'entraListing') {
        const r = await entraAppsApi.get();
        if (!r.success || !r.facts) {
          setError(r.error ?? 'Applications Entra ID indisponibles (permissions Graph ou tenant non connecté).');
          return;
        }
        const apps = r.facts.apps ?? [];
        if (apps.length === 0) { setNotice('Aucune App Registration trouvée sur le tenant.'); return; }
        await downloadEntraAppsListingDocx({ meta, facts: r.facts });
      } else if (key === 'exo') {
        const r = await m365ConfigFactsApi.exo();
        if (!r.success || !r.facts) { setError(r.error ?? 'Configuration Exchange Online indisponible (accès EXO ou tenant non connecté).'); return; }
        if (r.warnings && r.warnings.length > 0) setNotice(`Document généré — ${r.warnings.length} donnée(s) non récupérée(s) (accès/permissions).`);
        await downloadExoConfigDocx({ meta, facts: r.facts });
      } else if (key === 'spo') {
        const r = await m365ConfigFactsApi.spo();
        if (!r.success || !r.facts) { setError(r.error ?? 'Configuration SharePoint/OneDrive indisponible (permission Graph ou tenant non connecté).'); return; }
        await downloadSpoConfigDocx({ meta, facts: r.facts });
      } else if (key === 'teams') {
        const r = await m365ConfigFactsApi.teams();
        if (!r.success || !r.facts) { setError(r.error ?? 'Configuration Teams indisponible (permission Graph ou tenant non connecté).'); return; }
        if (r.warnings && r.warnings.length > 0) setNotice(`Document généré — ${r.warnings.length} donnée(s) non récupérée(s). Les politiques Teams sont à compléter manuellement.`);
        await downloadTeamsConfigDocx({ meta, facts: r.facts });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <PageHeader
        title={t('autodoc.title', 'AutoDoc')}
        description={t('autodoc.subtitle', 'Génération automatique de votre documentation de conformité NIS2 à partir de vos connexions Microsoft 365 et Azure.')}
      />

      <MessageBar intent="info" style={{ marginBottom: 16 }}>
        <MessageBarBody>
          Chaque document est structuré avec une <strong>introduction</strong> (objet & public cible), la même mise en forme, et votre <strong>logo</strong> (Organisation › Paramètres). Les documents Microsoft combinent le dernier scan et des faits lus en direct via Microsoft Graph.
        </MessageBarBody>
      </MessageBar>

      {error && <MessageBar intent="error" style={{ marginBottom: 16 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <MessageBar intent="warning" style={{ marginBottom: 16 }}><MessageBarBody>{notice}</MessageBarBody></MessageBar>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: 20, maxWidth: 1120 }}>
        <DocCard
          icon={<BuildingMultiple24Regular />} accent="#2E8B96"
          title="Plateforme Microsoft 365"
          sub="Global · Entra ID · Services (SharePoint, Exchange)"
          purpose="Configuration de la plateforme M365 : identité & accès (MFA, invités, app registrations), services, protection des données, journalisation."
          audience="RSSI, DPO, équipe IT/M365, auditeur."
          busy={busy === 'm365'} disabled={busy !== null}
          onGenerate={() => void run('m365')}
          warn={!hasM365Scan ? 'Aucun scan M365 — lancez un scan pour enrichir le document.' : undefined}
        />
        <DocCard
          icon={<BrandIcon name="azure" size={22} />} accent="#0089D6"
          title="Plateforme Microsoft Azure"
          sub="Réseau · Données · Calcul · Identité"
          purpose="État de configuration de l'environnement Azure au regard des exigences NIS2."
          audience="RSSI, équipe cloud/infrastructure, auditeur."
          busy={busy === 'azure'} disabled={busy !== null}
          onGenerate={() => void run('azure')}
          warn={!hasAzureScan ? 'Aucun scan Azure — connectez une souscription et lancez un scan.' : undefined}
        />
        <DocCard
          icon={<ShieldLock24Regular />} accent="#4A4A6E"
          title="Politique de sécurité générale"
          sub="Modèle ISMS conforme NIS2 (art. 21)"
          purpose="Modèle de politique de sécurité de l'information pré-rempli à partir de votre organisation, à valider par la direction."
          audience="Direction, RSSI, ensemble du personnel."
          busy={busy === 'policy'} disabled={busy !== null}
          onGenerate={() => void run('policy')}
          soon
        />
        <DocCard
          icon={<DocumentBulletList24Regular />} accent="#217346"
          title="Registre des traitements de données"
          sub="RGPD art. 30 — modèle pré-rempli (M365 / Azure)"
          purpose="Recensement des activités de traitement de données à caractère personnel, pré-rempli avec les services Microsoft."
          audience="DPO, responsable de traitement, RSSI."
          busy={busy === 'register'} disabled={busy !== null}
          onGenerate={() => void run('register')}
        />
        <DocCard
          icon={<Key24Regular />} accent="#8764B8"
          title="Configuration MFA actuelle"
          sub="Authentification forte & accès conditionnel (M365 / Azure)"
          purpose="État de l'authentification multifacteur, Conditional Access, auth legacy, comptes privilégiés/break-glass et PIM, agrégé depuis le dernier scan M365 et Azure."
          audience="RSSI, équipe IAM/identité, auditeur."
          busy={busy === 'mfa'} disabled={busy !== null}
          onGenerate={() => void run('mfa')}
          warn={!hasM365Scan && !hasAzureScan ? 'Aucun scan — lancez un scan M365/Azure pour alimenter le document.' : undefined}
        />
        <DocCard
          icon={<LockClosed24Regular />} accent="#C239B3"
          title="État du chiffrement des données"
          sub="Chiffrement au repos & en transit (M365 / Azure)"
          purpose="Étiquettes de confidentialité avec chiffrement, IRM/Message Encryption, Customer Key/Lockbox, TLS, CMK, TDE, BitLocker et protection des Key Vaults, agrégé depuis le dernier scan."
          audience="RSSI, DPO, équipe cloud/infrastructure, auditeur."
          busy={busy === 'encryption'} disabled={busy !== null}
          onGenerate={() => void run('encryption')}
          warn={!hasM365Scan && !hasAzureScan ? 'Aucun scan — lancez un scan M365/Azure pour alimenter le document.' : undefined}
        />
        <DocCard
          icon={<Mail24Regular />} accent="#0F6CBD"
          title="Configuration messagerie Exchange Online"
          sub="Domaines · SPF/DKIM/DMARC · anti-spam/phishing · connecteurs"
          purpose="Configuration de la messagerie Exchange Online : domaines acceptés, authentification email (SPF, DKIM, DMARC), politiques anti-spam et anti-phishing, connecteurs entrants/sortants. Lu en direct via l'API Exchange Online."
          audience="RSSI, équipe messagerie/M365, auditeur."
          busy={busy === 'exo'} disabled={busy !== null}
          onGenerate={() => void run('exo')}
        />
        <DocCard
          icon={<Share24Regular />} accent="#038387"
          title="Configuration SharePoint / OneDrive"
          sub="Partage externe · synchronisation · paramètres tenant"
          purpose="Paramètres tenant SharePoint et OneDrive : partage externe et restrictions de domaine, liens d'accès, synchronisation client et paramètres généraux. Lu en direct via Microsoft Graph."
          audience="RSSI, DPO, équipe collaboration/M365, auditeur."
          busy={busy === 'spo'} disabled={busy !== null}
          onGenerate={() => void run('spo')}
        />
        <DocCard
          icon={<PeopleTeam24Regular />} accent="#5059C9"
          title="Configuration des services Teams"
          sub="Inventaire équipes · accès invités & externe"
          purpose="Services Teams : inventaire des équipes et visibilité, accès invités et collaboration externe. Les politiques tenant (réunions, messagerie, appels, fédération) sont marquées « à compléter manuellement »."
          audience="RSSI, équipe collaboration/M365, auditeur."
          busy={busy === 'teams'} disabled={busy !== null}
          onGenerate={() => void run('teams')}
        />
        <DocCard
          icon={<AppsList24Regular />} accent="#005A9E"
          title="Inventaire des applications Entra ID"
          sub="Applications Entra ID (App Registrations)"
          purpose="Inventaire des applications Entra ID : audience, secrets/certificats et expirations, volume de permissions API, propriétaires. Lu en direct via Microsoft Graph."
          audience="RSSI, équipe IAM, auditeur."
          busy={busy === 'entraListing'} disabled={busy !== null}
          onGenerate={() => void run('entraListing')}
        />
      </div>
    </div>
  );
}

function DocCard({ icon, accent, title, sub, purpose, audience, busy, disabled, onGenerate, warn, soon }: {
  icon: React.ReactNode; accent: string; title: string; sub: string; purpose: string; audience: string;
  busy: boolean; disabled: boolean; onGenerate: () => void; warn?: string; soon?: boolean;
}) {
  return (
    <Card style={{ borderTop: `3px solid ${accent}`, display: 'flex', flexDirection: 'column', gap: 8, padding: 20, opacity: soon ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-flex', width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', background: `${accent}1A`, color: accent }}>{icon}</span>
        <div>
          <Title3 style={{ margin: 0, fontSize: 17 }}>{title}</Title3>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{sub}</Caption1>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <Body1 style={{ color: tokens.colorNeutralForeground2, display: 'block' }}><strong>Objet :</strong> {purpose}</Body1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 4 }}><strong>Public cible :</strong> {audience}</Caption1>
      </div>
      {warn && !soon && (
        <Caption1 style={{ color: '#A86317', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Warning24Regular style={{ width: 16, height: 16 }} /> {warn}
        </Caption1>
      )}
      <Button appearance={soon ? 'secondary' : 'primary'} icon={busy ? <Spinner size="tiny" /> : <ArrowDownload24Regular />} onClick={onGenerate} disabled={disabled || soon} style={{ alignSelf: 'flex-start' }}>
        {soon ? 'Bientôt disponible' : busy ? '…' : 'Générer (Word)'}
      </Button>
    </Card>
  );
}
